import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { D365TextField } from '@/components/D365FormControls';
import {
  getDefinitions,
  saveDefinition,
  type DefinitionInput,
  type DefinitionKind,
} from '@/features/settings/api/definitions';

const empty: DefinitionInput = { name: '', unit: '', min: 0, max: 100, step: 0.01 };

export function DefinitionFormPage() {
  const navigate = useNavigate();
  const params = useParams();
  const kind = params.kind as DefinitionKind;
  const isNew = params.definitionId === 'new';
  const definitionId = isNew ? null : Number(params.definitionId);
  const validKind = kind === 'readings' || kind === 'dosages';
  const query = useQuery({
    queryKey: ['settings', kind],
    queryFn: () => getDefinitions(kind),
    enabled: validKind && !isNew && Number.isInteger(definitionId) && Number(definitionId) > 0,
  });
  if (
    !validKind ||
    (!isNew && (!Number.isInteger(definitionId) || Number(definitionId) <= 0)) ||
    query.isError
  )
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This definition could not be loaded.
      </Alert>
    );
  if (!isNew && query.isLoading)
    return (
      <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label="Loading definition" />
      </Box>
    );
  const definition = isNew ? null : query.data?.find((item) => item.id === definitionId);
  if (!isNew && !definition)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This definition could not be loaded.
      </Alert>
    );
  const initial = definition
    ? {
        name: definition.name,
        unit: definition.unit,
        min: definition.min,
        max: definition.max,
        step: definition.step,
      }
    : empty;
  return (
    <DefinitionEditor
      key={`${kind}-${definitionId ?? 'new'}`}
      kind={kind}
      id={definitionId}
      initial={initial}
      onClose={() => void navigate('/settings')}
    />
  );
}

function DefinitionEditor({
  kind,
  id,
  initial,
  onClose,
}: {
  kind: DefinitionKind;
  id: number | null;
  initial: DefinitionInput;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    ...initial,
    min: String(initial.min),
    max: String(initial.max),
    step: String(initial.step),
  });
  const values = { ...form, min: Number(form.min), max: Number(form.max), step: Number(form.step) };
  const numericError = (value: string) => {
    if (!value.trim() || !Number.isFinite(Number(value))) return 'Enter a number.';
    const scaled = Number(value) * 100;
    if (
      !Number.isSafeInteger(Math.round(scaled)) ||
      Math.abs(scaled - Math.round(scaled)) > 0.000001
    ) {
      return 'Use up to two decimal places.';
    }
    return '';
  };
  const minError = numericError(form.min);
  const maxError =
    numericError(form.max) ||
    (!minError && values.max < values.min ? 'Maximum must be at least the minimum.' : '');
  const stepError =
    numericError(form.step) || (values.step <= 0 ? 'Step must be greater than zero.' : '');
  const [savedForm, setSavedForm] = useState(initial);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const singular = kind === 'readings' ? 'Reading' : 'Dosage';
  const save = useMutation({
    mutationFn: () => saveDefinition(kind, values, id ?? undefined),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['settings', kind] });
      setSavedForm(values);
      if (id === null) void navigate(`/settings/${kind}/${saved.id}`, { replace: true });
    },
  });
  const disabled =
    !form.name.trim() ||
    !form.unit.trim() ||
    Boolean(minError || maxError || stepError) ||
    save.isPending ||
    JSON.stringify(values) === JSON.stringify(savedForm);
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Stack direction="row" sx={{ minHeight: 48, px: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={onClose} sx={commandButtonSx}>
            Back
          </Button>
          <Button
            startIcon={<SaveOutlinedIcon />}
            disabled={disabled}
            onClick={() => save.mutate()}
            sx={commandButtonSx}
          >
            Save
          </Button>
        </Stack>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 2.5 }}>
        <Typography variant="h1">{id ? form.name || singular : `New ${singular}`}</Typography>
        <Typography color="text.secondary">{singular}</Typography>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 2, maxWidth: 620 }}>
        <Typography fontWeight={600} sx={{ mb: 2 }}>
          {singular.toUpperCase()} INFORMATION
        </Typography>
        <Stack spacing={2}>
          <D365TextField
            label="Name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
          />
          <D365TextField
            label="Unit of measurement"
            value={form.unit}
            onChange={(event) => setForm({ ...form, unit: event.target.value })}
            required
          />
          <Stack spacing={2}>
            <D365TextField
              type="number"
              slotProps={{ htmlInput: { step: 0.01 } }}
              label="Minimum"
              value={form.min}
              error={Boolean(minError)}
              helperText={minError}
              disabled={save.isPending}
              onChange={(event) => setForm({ ...form, min: event.target.value })}
            />
            <D365TextField
              type="number"
              slotProps={{ htmlInput: { step: 0.01 } }}
              label="Maximum"
              value={form.max}
              error={Boolean(maxError)}
              helperText={maxError}
              disabled={save.isPending}
              onChange={(event) => setForm({ ...form, max: event.target.value })}
            />
            <D365TextField
              type="number"
              slotProps={{ htmlInput: { step: 0.01, min: 0.01 } }}
              label="Step"
              value={form.step}
              error={Boolean(stepError)}
              helperText={stepError}
              disabled={save.isPending}
              onChange={(event) => setForm({ ...form, step: event.target.value })}
            />
          </Stack>
          {save.isError && <Alert severity="error">The definition could not be saved.</Alert>}
        </Stack>
      </Paper>
    </Box>
  );
}

const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
